import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { useVersionStoreySum } from '@client/hooks/use-element-expression-variables.hooks';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';

type ProjectBTASectionProps = {
  /* cost in SEK */
  cost: number | undefined;
};

/**
 * Footprint GFA and living area from the same {@link useVersionStoreySum} as {@link GFAChart}.
 */
export const ProjectGFASection: FC<ProjectBTASectionProps> = ({
  cost: sekCost,
}) => {
  const { gfa, la_apartments } = useVersionStoreySum();

  const gfaValueData = getDisplayValueData(
    { value: gfa ?? 0, unit: 'm^2' },
    { outputUnit: 'm^2' },
  );

  const displayCurrency = useDisplayCurrency();
  const costValueData = displayCurrency.isDisabled
    ? undefined
    : getDisplayValueData(
        { value: sekCost ?? 0, unit: 'SEK' },
        { outputUnit: displayCurrency.current, notation: 'compact' },
      );

  const livingAreaApartmentsValueData = getDisplayValueData(
    { value: la_apartments ?? 0, unit: 'm^2' },
    { outputUnit: 'm^2' },
  );

  return (
    <>
      <Box
        sx={{
          width: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
          }}
        >
          {gfaValueData.value}
        </Typography>
        <Typography variant="caption">{gfaValueData.unit} footprint</Typography>
      </Box>
      <Box
        sx={{
          width: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
          }}
        >
          {livingAreaApartmentsValueData.value}
        </Typography>
        <Typography variant="caption">
          {livingAreaApartmentsValueData.unit} LA
        </Typography>
      </Box>

      {costValueData && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
            }}
          >
            {costValueData.value}
          </Typography>
          <Typography variant="caption">
            {costValueData.unit} Total expenses
          </Typography>
        </Box>
      )}
    </>
  );
};
